import {
  iconAtendCelular,
  iconDadosCorretores,
  iconDocDocumento,
  iconFinCartao,
  iconNavConfig,
  iconSegurancaDesbloqueado,
} from '@bsds-lib-component/icons/icons';
import { BsdsCard, BsdsIcon } from '@bsds-lib-component/react';
import React, { useState } from 'react';

import { Button, SystemMessage, Visibility } from '../..';
import type { UseSubEstipulanteInfoDetailReturn } from '../../../hooks/vida/useSubEstipulanteInfo';
import type {
  SubEstipulanteCoverageItem,
  SubEstipulanteInfoField,
  SubEstipulanteInfoTab,
} from '../../../types/vida/cadastralMaintenance/subEstipulanteInfoTypes';
import type { ListaSubestipulanteItem } from '../../../types/vida/subEstipulante/subEstipulanteType';

type SubEstipulanteInfoSectionProps = {
  subestipulante: ListaSubestipulanteItem | null;
  info: UseSubEstipulanteInfoDetailReturn;
};

type InfoSectionCardProps = {
  title: string;
  icon: string;
  headerClassName: string;
  fields: SubEstipulanteInfoField[];
  highlightClassName: string;
};

type InfoFieldRowProps = {
  label: string;
  value: string;
  highlight?: boolean;
  highlightClassName?: string;
};

const INFO_TABS: Array<{ id: SubEstipulanteInfoTab; label: string }> = [
  { id: 'dados', label: 'Dados do Sub-Estipulante' },
  { id: 'cobertura', label: 'Coberturas' },
  { id: 'regras', label: 'Regras' },
];

const InfoSectionCard: React.FC<InfoSectionCardProps> = ({
  title,
  icon,
  headerClassName,
  fields,
  highlightClassName,
}) => {
  if (fields.length === 0) {
    return null;
  }

  return (
    <BsdsCard className="shadow-none border border-gray-100 rounded-md overflow-hidden" platform="web">
      <div className={`flex items-center gap-2 px-4 py-3 ${headerClassName}`}>
        <BsdsIcon icon={icon} className="w-4 h-4" aria-hidden="true" />
        <span className="text-sm font-semibold text-gray-800">{title}</span>
      </div>
      <div className="px-4 py-2">
        {fields.map((field) => (
          <InfoFieldRow
            key={field.label}
            label={field.label}
            value={field.value}
            highlight={field.highlight}
            highlightClassName={highlightClassName}
          />
        ))}
      </div>
    </BsdsCard>
  );
};

const InfoFieldRow: React.FC<InfoFieldRowProps> = ({
  label,
  value,
  highlight = false,
  highlightClassName = 'bg-gray-50',
}) => (
  <div
    className={`flex items-center justify-between gap-4 rounded-md px-3 py-3 mb-2 last:mb-0 ${highlight ? highlightClassName : 'bg-white'
      }`}
  >
    <span className="text-sm text-gray-500">{label}</span>
    <span className="text-sm font-semibold text-gray-900 text-right">{value}</span>
  </div>
);

const StatusBadge: React.FC<{ label: string; active?: boolean }> = ({
  label,
  active = true,
}) => {
  const variantClass = active ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-700';

  return (
    <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${variantClass}`}>
      {label}
    </span>
  );
};

const CoverageCapitalDetails: React.FC<{ cobertura: SubEstipulanteCoverageItem }> = ({ cobertura }) => {
  const details = [
    { label: 'Capital Uniforme', value: cobertura.capitalUniforme },
    { label: 'Limite de Capital', value: cobertura.limiteCapital },
    { label: 'Limite Fixo', value: cobertura.limiteFixo },
    { label: 'Percentual sobre Básica', value: cobertura.percentualBasica },
    { label: 'Prêmio', value: cobertura.premio },
  ].filter((detail) => detail.value);

  if (details.length === 0) {
    return null;
  }

  return (
    <div className="mt-2 space-y-1">
      {details.map((detail) => (
        <p key={detail.label} className="text-xs text-gray-500">
          {detail.label}: <span className="font-medium text-gray-700">{detail.value}</span>
        </p>
      ))}
    </div>
  );
};

const LimitesCapitalCard: React.FC<{ fields: SubEstipulanteInfoField[] }> = ({ fields }) => {
  if (fields.length === 0) {
    return null;
  }

  return (
    <BsdsCard
      className="shadow-none border border-gray-100 rounded-md overflow-hidden bsds-mt-24"
      platform="web"
    >
      <div className="flex items-center gap-2 px-4 py-3 bg-gray-100">
        <BsdsIcon icon={iconNavConfig} className="w-4 h-4" aria-hidden="true" />
        <span className="text-sm font-semibold text-gray-800">Limites de Capital</span>
      </div>
      <div className="px-4 py-2">
        {fields.map((field) => (
          <div key={field.label} className="mb-3 last:mb-0">
            <span className="text-xs text-gray-500 bsds-display-block bsds-mb-4">{field.label}</span>
            <div className={`rounded-md border border-gray-200 px-3 py-3 ${field.highlight ? 'bg-gray-50' : 'bg-white'}`}>
              <span className="text-sm font-semibold text-gray-900">{field.value}</span>
            </div>
          </div>
        ))}
      </div>
    </BsdsCard>
  );
};

const SubEstipulanteInfoSection: React.FC<SubEstipulanteInfoSectionProps> = ({
  info,
}) => {
  const [activeTab, setActiveTab] = useState<SubEstipulanteInfoTab>('dados');
  const { infoData, isLoading, error } = info;

  return (
    <div role="region" aria-label="Informações cadastrais do subestipulante" aria-busy={isLoading}>
      <div
        className="bsds-display-flex gap-4 bsds-mb-24 w-full max-w-3xl"
        role="tablist"
        aria-label="Seções de informações cadastrais do subestipulante"
      >
        {INFO_TABS.map((tab) => {
          const isActive = activeTab === tab.id;

          return (
            <Button
              key={tab.id}
              type="button"
              color="primary"
              fill={isActive ? 'solid' : 'outline'}
              onClick={() => setActiveTab(tab.id)}
              aria-controls={`subestipulante-info-${tab.id}`}
              aria-label={tab.label}
              aria-pressed={isActive}
              className="rounded-full flex-1 min-w-0 w-full"
            >
              {tab.label}
            </Button>
          );
        })}
      </div>

      {error && (
        <div className="bsds-mb-16">
          <SystemMessage message={error} type="error" />
        </div>
      )}

      {isLoading && (
        <p className="text-sm text-gray-500 bsds-mb-16">Carregando dados do Sub-Estipulante...</p>
      )}

      <Visibility isActive={activeTab === 'dados'} animated animationDuration={200}>
        <div
          id="subestipulante-info-dados"
          role="tabpanel"
          aria-label="Dados do subestipulante"
          className="grid grid-cols-1 lg:grid-cols-2 gap-6"
        >
          <InfoSectionCard
            title="Informações Cadastrais"
            icon={iconDocDocumento}
            headerClassName="bg-blue-50"
            fields={infoData.cadastrais}
            highlightClassName="bg-blue-50"
          />

          <InfoSectionCard
            title="Informações de Contato"
            icon={iconAtendCelular}
            headerClassName="bg-green-50"
            fields={infoData.contato}
            highlightClassName="bg-green-50"
          />

          <InfoSectionCard
            title="Endereço"
            icon={iconDadosCorretores}
            headerClassName="bg-pink-50"
            fields={infoData.endereco}
            highlightClassName="bg-pink-50"
          />

          <InfoSectionCard
            title="Informações Comerciais"
            icon={iconFinCartao}
            headerClassName="bg-amber-50"
            fields={infoData.comerciais}
            highlightClassName="bg-amber-50"
          />
        </div>
      </Visibility>

      <Visibility isActive={activeTab === 'cobertura'} animated animationDuration={200}>
        <div id="subestipulante-info-cobertura" role="tabpanel" aria-label="Coberturas do subestipulante">
          <BsdsCard
            className="shadow-none border border-green-100 rounded-md overflow-hidden"
            platform="web"
          >
            <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border-b border-green-100">
              <BsdsIcon icon={iconSegurancaDesbloqueado} className="w-4 h-4 text-green-700" aria-hidden="true" />
              <span className="text-sm font-semibold text-green-800">Coberturas Disponíveis</span>
            </div>
            <div className="p-4 space-y-3">
              {!isLoading && infoData.coberturas.length === 0 && (
                <p className="text-sm text-gray-500">Nenhuma cobertura encontrada para este subestipulante.</p>
              )}
              {infoData.coberturas.map((cobertura) => (
                <div
                  key={cobertura.id}
                  className={`flex items-center justify-between gap-4 rounded-md border px-4 py-3 ${cobertura.ativo
                      ? 'border-green-100 bg-green-50/40'
                      : 'border-gray-100 bg-gray-50'
                    }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <BsdsIcon
                      icon={iconSegurancaDesbloqueado}
                      className={`w-4 h-4 shrink-0 ${cobertura.ativo ? 'text-green-600' : 'text-gray-400'}`}
                      aria-hidden="true"
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900">{cobertura.nome}</p>
                      <p className="text-xs text-gray-500">Capital Segurado: {cobertura.capitalSegurado}</p>
                      <CoverageCapitalDetails cobertura={cobertura} />
                    </div>
                  </div>
                  <StatusBadge
                    label={cobertura.ativo ? 'Ativo' : 'Inativo'}
                    active={cobertura.ativo}
                  />
                </div>
              ))}
            </div>
          </BsdsCard>

          <LimitesCapitalCard fields={infoData.limitesCapital} />
        </div>
      </Visibility>

      <Visibility isActive={activeTab === 'regras'} animated animationDuration={200}>
        <div
          id="subestipulante-info-regras"
          role="tabpanel"
          aria-label="Regras do subestipulante"
          className="grid grid-cols-1 lg:grid-cols-2 gap-6"
        >
          <InfoSectionCard
            title="Regras de Adesão"
            icon={iconNavConfig}
            headerClassName="bg-amber-50"
            fields={infoData.regrasAdesao}
            highlightClassName="bg-amber-50"
          />

          <InfoSectionCard
            title="Regras de Capital"
            icon={iconNavConfig}
            headerClassName="bg-blue-50"
            fields={infoData.regrasCapital}
            highlightClassName="bg-blue-50"
          />

          {!isLoading && infoData.regrasAdesao.length === 0 && infoData.regrasCapital.length === 0 && (
            <p className="text-sm text-gray-500 lg:col-span-2">
              Nenhuma regra encontrada para este subestipulante.
            </p>
          )}
        </div>
      </Visibility>
    </div>
  );
};

export default SubEstipulanteInfoSection;
